import { createRetryFetch$, shareLast } from '@lseg-ciam/auth/utils';
import { Observable, of, switchMap } from 'rxjs';
import { pnsSubForEnv, type PnsSub } from '@heimdall/client';

// Env types and URL maps — moved from SDK to HLA (WS-specific)
const ENVS = ['dev', 'qa', 'ppe', 'prod'] as const;
type Env = (typeof ENVS)[number];

const issuerUrls: Record<Env, string> = {
  dev: 'https://token.dev-workspace.t-lseg.com/auth/v1',
  qa: 'https://token.qa-workspace.t-lseg.com/auth/v1',
  ppe: 'https://token.ppe-workspace.t-lseg.com/auth/v1',
  prod: 'https://token.workspace.lseg.com/auth/v1',
};

const idpLookupUrls: Record<Env, string> = {
  dev: 'https://identity.dev-ciam.t-lseg.com',
  qa: 'https://identity.qa-ciam.t-lseg.com',
  ppe: 'https://identity.ppe-ciam.t-lseg.com',
  prod: 'https://identity.ciam.lseg.com',
};

const searchParams = new URLSearchParams(document.location.search);
const appOrigin = document.location.origin;

// do NOT default here. this is required argument for Teams login.
const clientUid = searchParams.get('clientUid')?.trim();

const loginHint = searchParams.get('loginHint') || undefined;
const prompt = searchParams.get('prompt') || undefined;
const forceRedirect = searchParams.get('forceRedirect') === 'true';
const webLoginUrl = location.origin + import.meta.env.BASE_URL + '/index.html';
const teamsLoginUrl =
  location.origin + import.meta.env.BASE_URL + '/teams/index.html';
const redirectUri =
  location.origin + import.meta.env.BASE_URL + '/redirect/index.html';
const linkRedirectUri =
  location.origin + import.meta.env.BASE_URL + '/redirect/link.html';

const linkOriginKey = searchParams.get('klo') || undefined;
const silent = searchParams.get('silent') === 'true';
const idp = searchParams.get('idp') || undefined;
const userId = searchParams.get('userId') || undefined;
const lane = searchParams.get('lane') || undefined;
const persistTokens = searchParams.get('persistTokens') === 'true';

const nextPathParam = (searchParams.get('nextPath') || '').trim();
const nextPath =
  nextPathParam.indexOf('#') === -1
    ? nextPathParam + document.location.hash
    : nextPathParam;
const recoverPath = searchParams.get('recoverPath') || undefined;
const postLogoutUrl =
  location.origin + import.meta.env.BASE_URL + '/logout/index.html';

type EnvFile = {
  environment: Env;
};

export type AppConfig = {
  issuerUrl: string;
  clientUid?: string;
  redirectUri: string;
  linkRedirectUri: string;
  linkOriginKey?: string;
  nextPath: string;
  recoverPath?: string;
  webLoginUrl: string;
  teamsLoginUrl: string;
  postLogoutUrl: string;
  forceRedirect: boolean;
  persistTokens?: boolean;
  loginHint?: string;
  prompt?: string;
  silent?: boolean;
  idp?: string;
  userId?: string;
  idpLookupUrl: string;
  lane?: string;
  env: Env;
  pnsSub: PnsSub;
};

const appConfig$: Observable<AppConfig> = createRetryFetch$<EnvFile>(
  '/public/env.json'
).pipe(
  switchMap(({ environment: env }) => {
    return of({
      issuerUrl: issuerUrls[env],
      idpLookupUrl: idpLookupUrls[env],
      ...(clientUid && { clientUid }),
      ...(idp && { idp }),
      ...(userId && { userId }),
      ...(lane && { lane }),
      ...(persistTokens && { persistTokens }),
      redirectUri,
      linkRedirectUri,
      linkOriginKey,
      nextPath,
      recoverPath,
      webLoginUrl,
      teamsLoginUrl,
      postLogoutUrl,
      forceRedirect,
      loginHint,
      prompt,
      silent,
      env,
      pnsSub: pnsSubForEnv(env),
    });
  }),
  shareLast
);

function isNextPathValid(nextPath: string): boolean {
  return !!nextPath && new URL(nextPath, appOrigin).origin === appOrigin;
}

export { appConfig$, isNextPathValid, issuerUrls, idpLookupUrls, type Env };
